import React, {useMemo, useState} from 'react';
import {Button, ButtonGroup, Stack, Typography} from "@mui/material";
import {useSearchParams} from "react-router";
import {useSession} from '../../firebase/UserProvider';
import {useReportStore} from '../../features/products/useReportStore';
import SourcePackageDropdownMenu from '../DocumentContextMenus/SourcePackageDropdownMenu';
import SourcePackageSubmenu from '../DocumentContextMenus/SourcePackageSubmenu';
import MoveFolderDialog from '../sourcePackage/FolderRow/MoveFolderDialog';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import {computeLocalId, refKey} from './utils';
import {isProductReportSource} from './sourceCitePolicy';
import {createReferenceMarkWithFirestore, getExactlyMatchingMetaMark} from '../prosemirror-editor';
import {addReference, getReferencesForMark} from '../prosemirror-editor/yMaps/referencesYMap';
import useYDocStore from '../../stores/useYDocStore';
import {useYSourcePackage} from '../sourcePackage/hooks';
import {getFolderById} from '../prosemirror-editor/yMaps/sourcePackageYMap';

/**
 * Hook to check if a segment is already cited in a specific reference mark.
 * Reads directly from Y.Map — no Firestore subscription needed.
 */
function useIsAlreadyCited({elementId, docId, segmentId, offset, index, start, end}) {
    const yReferenceMarks = useYDocStore(state => state.yReferenceMarks);

    return useMemo(() => {
        if (!elementId || !yReferenceMarks) return false;
        const currentLocalId = computeLocalId({docId, index, offset, start, end});
        const currentKey = refKey({docId, segmentId, offset});
        const refs = getReferencesForMark(yReferenceMarks, elementId);
        return refs.some(r =>
            (currentLocalId && r.localId === currentLocalId) || refKey(r) === currentKey
        );
    }, [yReferenceMarks, elementId, docId, segmentId, offset, index, start, end]);
}

/**
 * SegmentActions component - handles "Save" and "Cite this" buttons for search results
 */
function SegmentActions({
                            segmentType,
                            reportId,
                            savedSegmentIndex: _savedSegmentIndex,
                            setSegments: _setSegments,
                            // Source package item actions (for segmentType === 'saved')
                            itemId,
                            folders: foldersProp,
                            moveItem,
                            removeItem,
                            identifiers
                        }) {
    const {
        docId,
        segmentId,
        index,
        offset,
        start,
        end,
        localId: explicitLocalId,
        docSourceType,
        reportId: sourceReportId,
        productType,
    } = identifiers || {};

    const {user} = useSession();
    const isSelectedText = useReportStore(s => s.isSelectedText);
    const editorView = useReportStore(s => s.editorView);
    const activeReferenceMarkerId = useReportStore(s => s.activeReferenceMarkerId);

    // Source package data from Yjs (replaces removed useReportStore sourcePackage selectors)
    const {items: spItems, folders: spFolders, addItem: addSpItem, addFolder: yAddFolder} = useYSourcePackage();
    const ySourcePackage = useYDocStore(s => s.ySourcePackage);

    const [searchParams] = useSearchParams();
    const elementName = searchParams.get('elementName');
    const elementId = searchParams.get('elementId');

    // Determine the target reference ID: URL param takes precedence, then active reference marker at caret
    const targetReferenceId = (elementName === 'report-fragment' && elementId)
        ? elementId
        : activeReferenceMarkerId;

    const isAlreadyAdded = useIsAlreadyCited({
        elementId: targetReferenceId,
        docId,
        segmentId,
        offset,
        index,
        start,
        end
    });

    const hasSelectedFragment = !!elementName && elementName === 'report-fragment' && !!elementId;
    const hasActiveReference = !!activeReferenceMarkerId;
    const citeTargetAvailable = hasSelectedFragment || hasActiveReference || !!isSelectedText;
    const isReportProductSource = isProductReportSource({
        docSourceType,
        reportId: sourceReportId,
        localId: explicitLocalId,
        productType,
    });
    const canCite = citeTargetAvailable && !isReportProductSource;

    const localId = useMemo(() => (
        explicitLocalId || computeLocalId({docId, index, offset, start, end})
    ), [explicitLocalId, docId, index, offset, start, end]);

    const isAlreadySaved = useMemo(() => {
        if (!localId) return false;
        return (spItems || []).some(it => it?.localId === localId);
    }, [spItems, localId]);

    const savedFolderName = useMemo(() => {
        if (!localId) return null;
        const item = (spItems || []).find(it => it?.localId === localId);
        if (!item) return null;
        const fid = (item.parentFolderId === undefined ? null : item.parentFolderId);
        if (fid == null) return 'Root';
        const folder = getFolderById(ySourcePackage, fid);
        return folder?.name || 'Unknown folder';
    }, [spItems, localId, ySourcePackage]);

    const [saveAnchorEl, setSaveAnchorEl] = useState(null);
    const saveMenuOpen = Boolean(saveAnchorEl);
    const [fullDialogOpen, setFullDialogOpen] = useState(false);
    const [folderRefreshTrigger, setFolderRefreshTrigger] = useState(0);

    // Move dialog state (for segmentType === 'saved')
    const [moveDialogOpen, setMoveDialogOpen] = useState(false);
    const [moveTarget, setMoveTarget] = useState('');

    // Move options for 'saved' items — always computed (hooks must not be conditional)
    const savedMoveOptions = useMemo(() => {
        const sorted = (foldersProp || [])
            .slice()
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            .map(f => ({id: f.id, name: f.name || 'Untitled'}));
        return [{id: '', name: '(Root)'}, ...sorted];
    }, [foldersProp]);

    const handleOpenSaveMenu = (e) => {
        if (!localId || isAlreadySaved) return;
        setSaveAnchorEl(e.currentTarget);
    };
    const handleCloseSaveMenu = () => setSaveAnchorEl(null);

    const handleChooseAnother = () => {
        handleCloseSaveMenu();
        setFullDialogOpen(true);
    };

    const addToFolder = (folderId = null) => {
        if (!localId || isAlreadySaved) return;
        try {
            addSpItem({type: 'snippet', parentFolderId: folderId, localId, userId: user?.uid, ...identifiers});
        } catch (e) {
            console.error('Failed to save to source package:', e);
        } finally {
            handleCloseSaveMenu();
        }
    };

    const handleCreateFolder = async (parentFolderId = null, _productId = null) => {
        const name = window.prompt('Enter folder name:');
        if (!name) return;
        try {
            const id = yAddFolder({name, parentFolderId, userId: user?.uid});
            setFolderRefreshTrigger(prev => prev + 1);
            return id;
        } catch (e) {
            console.error('Failed to create folder:', e);
            return undefined;
        }
    };

    async function handleAddAsReference() {
        if (isAlreadyAdded || isReportProductSource) return;

        let targetFragmentId = null;

        // Priority: URL param > active reference marker at caret > create new from selection
        if (elementName === 'report-fragment' && elementId) {
            targetFragmentId = elementId;
        } else if (activeReferenceMarkerId) {
            // Use the active reference marker at caret position (highest start position)
            targetFragmentId = activeReferenceMarkerId;
        } else if (isSelectedText && editorView) {
            // Check if there's already a reference mark that exactly matches this selection
            try {
                const exactMatchUid = getExactlyMatchingMetaMark(editorView.state, 'reference');
                if (exactMatchUid) {
                    // Use the existing reference mark that exactly matches
                    targetFragmentId = exactMatchUid;
                    console.log('Using existing reference mark with exact match:', targetFragmentId);
                } else {
                    // Create a new reference mark at the current selection in ProseMirror
                    // Also creates the Firestore referenceMarks document
                    const uid = await createReferenceMarkWithFirestore(editorView, reportId);
                    if (uid) targetFragmentId = uid;
                }
            } catch (e) {
                console.error('Failed to create/get reference mark:', e);
            }
        }

        if (!targetFragmentId) return;

        // Use the resolved localId (explicit for federated hits, computed for corpus).
        const referenceData = {...identifiers, localId};

        // Write to Y.Map — instant, no Firestore needed
        const {ydoc, yReferenceMarks} = useYDocStore.getState();
        if (!ydoc || !yReferenceMarks) {
            console.warn('[SegmentActions] Y.Doc not ready');
            return;
        }
        addReference(ydoc, yReferenceMarks, targetFragmentId, referenceData);

        // Also add to Source Package root if not already in ANY folder
        try {
            if (localId) {
                const existsAnywhere = (spItems || []).some(it => it?.localId === localId);
                if (!existsAnywhere) {
                    addSpItem({type: 'snippet', parentFolderId: null, localId, userId: user?.uid, ...identifiers});
                }
            }
        } catch (_) { /* ignore add-to-package errors */
        }
    }

    if (segmentType === 'search') {
        return (
            <>
                <Stack direction={'row'} spacing={1} justifyContent={'space-between'} alignItems={'center'}>
                    {/* Left: Save to Source Package */}
                    <Stack direction={'row'} spacing={1} alignItems={'center'}>
                        {isAlreadySaved ? (
                            <Typography variant="body2"
                                        color={'success.main'}>{`Saved to: ${savedFolderName}` || 'Saved'}</Typography>
                        ) : (
                            <ButtonGroup variant="outlined" size={'small'}>
                                <Button onClick={(e) => {
                                    e.stopPropagation();
                                    addToFolder(null);
                                }}>Save</Button>
                                <Button
                                    variant="outlined"
                                    sx={{p: 0.5}}
                                    disabled={!localId || isAlreadySaved}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenSaveMenu(e);
                                    }}>
                                    <KeyboardArrowDownRoundedIcon/>
                                </Button>
                            </ButtonGroup>
                        )}
                    </Stack>

                    {/* Right: Cite this (Products/reports are not citable as evidence) */}
                    {!isReportProductSource && (
                        <Stack direction={'row'} spacing={1} alignItems={'center'}>
                            {isAlreadyAdded ? (
                                <Typography variant="body2" color={'success.main'}>Cited</Typography>
                            ) : (
                                <Button
                                    disabled={!canCite || isAlreadyAdded}
                                    sx={{flexShrink: 0}}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleAddAsReference();
                                    }}
                                >Cite this</Button>
                            )}
                        </Stack>
                    )}
                </Stack>

                {/* Dropdown menu with suggested folders */}
                <SourcePackageDropdownMenu
                    open={saveMenuOpen}
                    anchorEl={saveAnchorEl}
                    folderList={spFolders}
                    onAddToRoot={() => addToFolder(null)}
                    onAddToFolder={(fid) => addToFolder(fid)}
                    onChooseAnother={handleChooseAnother}
                    onClose={handleCloseSaveMenu}
                />

                {/* Full dialog for browsing all folders */}
                <SourcePackageSubmenu
                    open={fullDialogOpen}
                    folderList={spFolders}
                    onAddToRoot={() => {
                        addToFolder(null);
                        setFullDialogOpen(false);
                    }}
                    onAddToFolder={(fid) => {
                        addToFolder(fid);
                        setFullDialogOpen(false);
                    }}
                    onAddToAnotherProduct={(productId, folderId) => {
                        console.log('Add to another product:', productId, folderId);
                    }}
                    onCreateNewFolder={handleCreateFolder}
                    onClose={() => setFullDialogOpen(false)}
                    folderRefreshTrigger={folderRefreshTrigger}
                />
            </>
        );
    }

    if (segmentType === 'saved') {
        const handleOpenMoveDialog = () => {
            setMoveTarget('');
            setMoveDialogOpen(true);
        };

        const handleConfirmMove = () => {
            const newParent = moveTarget === '' ? null : moveTarget;
            moveItem?.(itemId, newParent);
            setMoveDialogOpen(false);
        };

        const handleRemove = () => {
            removeItem?.(itemId);
        };

        return (
            <>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" variant="outlined" onClick={handleOpenMoveDialog}>
                        Move
                    </Button>
                    <Button size="small" variant="outlined" color="error" onClick={handleRemove}>
                        Remove
                    </Button>
                </Stack>

                <MoveFolderDialog
                    open={moveDialogOpen}
                    folderId={itemId}
                    currentTarget={moveTarget}
                    moveOptions={savedMoveOptions}
                    onTargetChange={setMoveTarget}
                    onClose={() => setMoveDialogOpen(false)}
                    onConfirm={handleConfirmMove}
                />
            </>
        );
    }

    return null;
}

export default React.memo(SegmentActions);

